/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import Card from '@/components/card';
import BreadcrumbComponent from '@/components/breadcrumb';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  notification,
  Row,
  Table,
} from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { PlusIcon } from '@heroicons/react/24/solid';
import { DeleteFilled } from '@ant-design/icons';
import { IStock } from '@/interfaces/stock';
import { useMutation, useQuery } from '@tanstack/react-query';
import { round, uniqueId } from 'lodash';
import StockSelectorModal from '@/components/stock/stock-selector-modal';
import CustomerSelector from '@/components/selector/customer-selector';
import { ICustomer } from '@/interfaces/customer';
import { IStockReserved, IStockReservedItem } from '@/interfaces/reserved';
import {
  addStockReserved,
  editStockReserved,
  getStockReserved,
} from '@/adapters/reserved';
import { formatNumber } from '@/utils/dataUtils';

const MaintainReserved = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [mainForm] = Form.useForm<Partial<IStockReserved>>();
  const [form] = Form.useForm();
  const [data, setData] = useState<Partial<IStockReservedItem>[]>([]);
  const [editingKey, setEditingKey] = useState<string>('');
  const [editingIndex, setEditingIndex] = useState<string>('');
  const [isAddStock, setIsAddStock] = useState(false);
  const prefillStock = (location.state as { stock?: IStock } | null)?.stock;

  const { data: reserved } = useQuery<IStockReserved, Error>({
    queryKey: ['get-reserved'],
    queryFn: async () => await getStockReserved(Number(id)),
    enabled: id !== 'create',
  });

  const _customerId = Form.useWatch('customerId', mainForm);

  const isEditing = (record: Partial<IStockReservedItem>) =>
    record.key === editingKey;

  const edit = (record: Partial<IStockReservedItem>) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.key || '');
  };

  const save = async (key: string) => {
    try {
      const row = (await form.validateFields()) as Partial<IStockReservedItem>;
      const newData = [...data];
      const index = newData.findIndex(item => key === item.key || '');
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          // unitPrice: round(Number(item.unitPrice || 0), 2),
          quantity: round(Number(item.quantity || 0), 2),
          ...row,
        });
        setData(newData);
        setEditingKey('');
        setEditingIndex('');
      } else {
        newData.push(row);
        setData(newData);
        setEditingIndex('');
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  interface EditableCellProps extends React.HTMLAttributes<any> {
    editing: boolean;
    dataIndex: string;
    title: string;
    inputType: 'number' | 'text';
    record: Partial<IStockReservedItem>;
    index: number;
  }

  const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
    record,
    editing,
    dataIndex,
    title,
    inputType,
    children,
    ...restProps
  }) => {
    const inputNode =
      inputType === 'number' ? (
        <InputNumber
          className="w-full"
          type="number"
          onBlur={() => save(record.key || '')}
          onPressEnter={() => save(record.key || '')}
          autoFocus
          controls={false}
          decimalSeparator={'.'}
          precision={2}
          style={{ textAlignLast: 'right' }}
        />
      ) : (
        <Input onBlur={() => save(record.key || '')} />
      );

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: `Please Input ${title}!`,
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const columns = [
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      width: 120,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'สินค้า',
      dataIndex: 'productName',
      className: 'font-bold',
    },
    {
      title: 'Lot Number',
      dataIndex: 'stockCode',
      width: 120,
      className: 'font-bold',
      align: 'center',
    },

    {
      title: 'จำนวน Package',
      width: 120,
      dataIndex: 'package',
      align: 'right',
      render: (_: number, rec: IStockReservedItem) =>
        formatNumber(round(rec.quantity / rec.conversion, 2), 2),
    },
    {
      title: 'หน่วย Package',
      width: 120,
      dataIndex: 'packageUnitId',
    },
    {
      title: 'จำนวน',
      width: 120,
      editable: editingIndex === 'quantity',
      dataIndex: 'quantity',
      className: 'font-bold text-green-700',
      align: 'right',
      render: (_: number, rec: IStockReservedItem) => (
        <div
          className="font-bold w-full cursor-pointer"
          onClick={() => onRowClick(rec, 'quantity')}
        >
          {formatNumber(_, 2)}
        </div>
      ),
    },
    {
      title: 'หน่วยขาย',
      width: 100,
      dataIndex: 'baseUnitId',
      className: 'font-bold',
    },
    {
      title: '',
      width: 40,
      dataIndex: 'action',
      align: 'center',
      render: (_: string, rec: IStockReservedItem) => (
        <DeleteFilled
          className="text-red-700"
          onClick={() => onDeleteOrderItem(rec)}
        />
      ),
    },
  ];

  const mergedColumns: any = columns.map(col => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: IStockReservedItem) => ({
        record,
        inputType: col.dataIndex === editingIndex ? 'number' : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const onAddOrderItem = (items: IStock[]) => {
    const orderItems: Partial<IStockReservedItem>[] = items.map(i => {
      const orderItem: Partial<IStockReservedItem> = {
        ...i,
        key: uuidv4(),
        id: 0,
        stockId: i.id,
        quantity: i.quantity,
        stockCode: i.code,
        packUnitId: i.packageUnitName,
        baseUnitId: i.baseUnitName,
      };
      return orderItem;
    });
    setData([...data, ...orderItems]);
  };

  const onDeleteOrderItem = (item: IStockReservedItem) => {
    setData(prev => prev.filter(p => p.key !== item.key));
  };

  const onRowClick = (record: Partial<IStockReservedItem>, index: string) => {
    edit(record);
    setEditingIndex(index);
  };

  const mutation = useMutation({
    mutationFn: async (data: Partial<IStockReserved>) =>
      id !== 'create'
        ? await editStockReserved(data)
        : await addStockReserved(data),
    mutationKey: ['add-stock-reserved'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        notification.success({
          message: 'บันทึกรายการจองร์สำเร็จ',
        });
        navigate(`/reserved/${data?.data?.id}`);
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    mainForm
      .validateFields()
      .then(async (values: Partial<IStockReserved>) => {
        if (!data.length) {
          message.warning('กรุณาเลือกรายการ');
          return;
        }
        values.stockReservedItems = data.map(d => {
          const orderItem: Partial<IStockReservedItem> = {
            ...d,
            id: 0,
            stockReservedId: values.id,
            isActive: true,
          };
          return orderItem;
        });
        values.isActive = true;
        await mutation.mutateAsync(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const onCustomerChange = (customerInfo: ICustomer) => {
    mainForm.setFieldsValue({
      customerId: customerInfo.id,
      customerName: customerInfo.name,
      customerCode: customerInfo.code,
    });
  };

  useEffect(() => {
    if (id == 'create') {
      mainForm.resetFields();
      if (prefillStock) {
        const netRemain =
          Number(prefillStock.remain || 0) -
          Number(prefillStock.reservedQuantity || 0);
        setData([
          {
            key: uuidv4(),
            id: 0,
            stockId: prefillStock.id,
            productId: prefillStock.productId,
            quantity: Math.max(netRemain, 0),
            conversion: prefillStock.conversion,
            stockCode: prefillStock.code,
            productCode: prefillStock.productCode,
            productName: prefillStock.productName,
            packUnitId: prefillStock.packageUnitName,
            baseUnitId: prefillStock.baseUnitName,
            isActive: true,
          },
        ]);
      } else {
        setData([]);
      }
    }
  }, [id]);

  useEffect(() => {
    if (id !== 'create' && reserved) {
      mainForm.setFieldsValue({ ...reserved });
      setData(
        reserved.stockReservedItems?.map(c => {
          c.key = uniqueId('reserved-');
          return c;
        }),
      );
    }
  }, [reserved, id]);

  return (
    <Card>
      <BreadcrumbComponent
        items={[
          {
            title: (
              <div
                className="cursor-pointer"
                onClick={() => navigate(`/reserved`)}
              >
                จองสินค้า
              </div>
            ),
          },
          {
            title: 'สร้างออเดอร์ใหม่',
          },
        ]}
      />
      <div className="flex w-full mt-5">
        <Form
          form={mainForm}
          layout="vertical"
          className="w-full"
          autoComplete="off"
        >
          <Form.Item<IStockReserved> name="id" hidden>
            <Input type="hidden" />
          </Form.Item>
          {/* <Form.Item<IStockReserved> name="customerId" hidden>
            <Input type="hidden" />
          </Form.Item> */}
          <Form.Item<IStockReserved> name="customerCode" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item<IStockReserved> name="customerName" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Row gutter={24}>
            <Col md={24} lg={16} xxl={18}>
              <Row gutter={12}>
                <Col xs={24} md={24} lg={12} xxl={8}>
                  <Form.Item
                    label="ลูกค้า"
                    name="customerId"
                    rules={[{ required: true, message: 'เลือกลูกค้า' }]}
                  >
                    <CustomerSelector handleSelectData={onCustomerChange} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={6} xxl={4}>
                  <Form.Item<IStockReserved>
                    label="เลขที่ใบแจ้งหนี้ (IV)"
                    name="ivNo"
                  >
                    <Input size="large" placeholder="เลขที่ใบแจ้งหนี้" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={6} xxl={4}>
                  <Form.Item<IStockReserved>
                    label="เลขที่อ้างอิงใบสั่งซื้อ (PO)"
                    name="poNo"
                  >
                    <Input size="large" placeholder="เลขที่ใบสั่งซื้อ" />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </div>
      <div className="flex w-full mt-5">
        <Form form={form} component={false}>
          <Table
            components={{
              body: {
                cell: EditableCell,
              },
            }}
            locale={{
              emptyText: 'เพิ่มรายการใหม่',
            }}
            size="middle"
            bordered
            dataSource={data}
            columns={mergedColumns}
            className="w-full"
            rowClassName="editable-row"
            pagination={false}
            scroll={{ x: 1200 }}
          />
        </Form>
      </div>
      <div className="flex justify-end mt-3">
        <Button
          disabled={!_customerId}
          type="default"
          className="text-blue-700 font-bold"
          onClick={() => setIsAddStock(true)}
        >
          <PlusIcon className="w-3 h-3" /> เพิ่มรายการ
        </Button>
        <StockSelectorModal
          handleCancel={() => setIsAddStock(false)}
          handleOk={onAddOrderItem}
          open={isAddStock}
          exists={[]}
          customerId={Number(_customerId)}
        />
      </div>
      <div className="flex justify-end mt-5 gap-2">
        <Button
          ghost
          type="primary"
          htmlType="submit"
          size="large"
          className=" font-bold min-w-[150px]"
          loading={mutation.isPending}
          onClick={() => handleUpdate()}
        >
          บันทึก
        </Button>
      </div>
    </Card>
  );
};

export default MaintainReserved;
